import React from 'react'
import { ChunkLoadRecoveryPanel } from './ChunkLoadRecoveryPanel'
import { UnexpectedErrorFallback } from './UnexpectedErrorFallback'
import { autoRecoverFromChunkLoadError, isChunkLoadError } from 'helpers/chunkLoadRecovery'
import { recordClientError } from 'services/clientDiagnostics'

interface ErrorBoundaryProps {
 children: React.ReactNode
 fallback?: React.ReactNode
}

interface ErrorBoundaryState {
 hasError: boolean
 error?: Error
 isChunkLoadError: boolean
 retryingSoft: boolean
 supportCode?: string
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
 constructor(props: ErrorBoundaryProps) {
 super(props)
 this.state = { hasError: false, isChunkLoadError: false, retryingSoft: false }
 }

 static getDerivedStateFromError(error: Error): ErrorBoundaryState {
 return {
 hasError: true,
 error,
 isChunkLoadError: isChunkLoadError(error),
 retryingSoft: false,
 }
 }

 componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
 if (isChunkLoadError(error)) {
 void autoRecoverFromChunkLoadError()
 } else {
 console.error('ErrorBoundary caught:', error, errorInfo)
 void recordClientError('react_error_boundary', error, {
 severity: 'critical',
 category: 'crash',
 metadata: { phase: 'react_render' },
 }).then((event) => {
 if (event?.support_code) this.setState({ supportCode: event.support_code })
 })
 }
 }

 render() {
 if (this.state.hasError) {
 if (this.state.isChunkLoadError) {
 return (
 <ChunkLoadRecoveryPanel
 isOffline={typeof navigator !== 'undefined' && !navigator.onLine}
 retryingSoft={this.state.retryingSoft}
 onSoftRetry={() => {
 this.setState({ retryingSoft: true })
 window.setTimeout(() => {
 this.setState({
 hasError: false,
 error: undefined,
 isChunkLoadError: false,
 retryingSoft: false,
 })
 }, 200)
 }}
 />
 )
 }
 if (this.props.fallback) return this.props.fallback
 return (
 <UnexpectedErrorFallback
 supportCode={this.state.supportCode}
 onRetry={() =>
 this.setState({ hasError: false, error: undefined, isChunkLoadError: false, retryingSoft: false, supportCode: undefined })
 }
 />
 )
 }
 return this.props.children
 }
}
